import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { MenuOptionModal } from '../MenuOptionModal';
import { OrderConfirmationModal } from '../OrderConfirmationModal';
import type { CartItem, SelectedOption, CartItemInput, MenuItemWithOptions } from '@/types';
import type { cartTranslations } from '@/i18n';

interface CartModalsProps {
  // Clear Cart Confirmation
  showClearConfirm: boolean;
  onClearConfirmClose: () => void;
  onClearConfirm: () => void;

  // Delete Item Confirmation
  showDeleteConfirm: boolean;
  itemToDelete: string | null;
  onDeleteConfirmClose: () => void;
  onDeleteConfirm: () => void;

  // Menu Option Modal (for editing)
  showOptionModal: boolean;
  editingItem: CartItem | null;
  parsedCustomizations: {
    selectedOptions: Record<string, string[]>;
  };
  onOptionModalClose: () => void;
  onUpdateCartItemOptions: (
    itemId: string,
    updatedItem: CartItemInput | null,
    quantity: number,
    selectedOptions?: SelectedOption[]
  ) => void;
  createMenuItemFromCartItem: (cartItem: CartItem) => MenuItemWithOptions;

  // Order Confirmation Modal
  showOrderConfirmation: boolean;
  onOrderConfirmationClose: () => void;

  // Common props
  t: typeof cartTranslations['ko'];
}

/**
 * 장바구니 모달들 통합 컴포넌트
 * - Clear Cart Confirmation
 * - Delete Item Confirmation
 * - Menu Option Modal (MenuOptionModal import)
 * - Order Confirmation Modal
 */
export function CartModals({
  // Clear Cart Confirmation
  showClearConfirm,
  onClearConfirmClose,
  onClearConfirm,

  // Delete Item Confirmation
  showDeleteConfirm,
  itemToDelete: _itemToDelete,
  onDeleteConfirmClose,
  onDeleteConfirm,

  // Menu Option Modal
  showOptionModal,
  editingItem,
  parsedCustomizations,
  onOptionModalClose,
  onUpdateCartItemOptions,
  createMenuItemFromCartItem,

  // Order Confirmation Modal
  showOrderConfirmation,
  onOrderConfirmationClose,

  // Common props
  t,
}: CartModalsProps) {
  return (
    <>
      {/* Clear Cart Confirmation */}
      {showClearConfirm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-20">
          <Card className="bg-background p-6 rounded-2xl max-w-sm w-full">
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t.confirmClear}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t.confirmClearDescription}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClearConfirmClose}
                className="flex-1"
              >
                {t.cancel}
              </Button>
              <Button
                onClick={onClearConfirm}
                className="flex-1 bg-destructive hover:bg-destructive-dark text-white"
              >
                {t.confirm}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Menu Option Modal for editing cart items */}
      {/* #65: apiOptions로 원본 옵션 데이터 전달 */}
      {editingItem && (
        <MenuOptionModal
          item={createMenuItemFromCartItem(editingItem)}
          isOpen={showOptionModal}
          onClose={onOptionModalClose}
          onAddToCart={(updatedItem, quantity, selectedOptions) =>
            onUpdateCartItemOptions(editingItem.id, updatedItem, quantity, selectedOptions)
          }
          initialQuantity={editingItem.quantity}
          initialSelectedOptions={parsedCustomizations.selectedOptions}
          // #497: 입력형 옵션 사용자 입력값 복원 — cart 편집 시 silent 누락 차단.
          initialInputValues={editingItem.inputValues}
          apiOptions={editingItem._originalOptions}
        />
      )}

      {/* Order Confirmation Modal for Table Orders */}
      <OrderConfirmationModal
        isOpen={showOrderConfirmation}
        onClose={onOrderConfirmationClose}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={onDeleteConfirmClose}>
        <AlertDialogContent className="bg-background border-border rounded-[5px] max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[18px] text-foreground">
              {t.confirmDelete}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] text-muted-foreground">
              {t.confirmDeleteDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2">
            <AlertDialogCancel
              onClick={() => {
                onDeleteConfirmClose();
              }}
              className="flex-1 h-[50px] text-[14px] font-medium rounded-[5px] border-2 border-border text-muted-foreground bg-background hover:bg-muted hover:border-background-secondary transition-all duration-200 active:scale-95 flex items-center justify-center"
            >
              {t.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteConfirm}
              className="flex-1 h-[50px] text-[14px] font-medium rounded-[5px] bg-destructive text-white hover:bg-destructive-dark transition-all duration-200 active:scale-95 shadow-sm flex items-center justify-center"
            >
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
